import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Button, Image, Input, message, Modal, Typography, Upload } from "antd";

import { EditOutlined, EyeOutlined, PlusOutlined } from "@ant-design/icons";

import {
  createBrandContent,
  getBrandContentsByType,
} from "../../../apis/BrandContentApi";
import { setLoading } from "../../../redux/store/common/commonDuck";
import { plutusS3 } from "../../../utils/plutusS3";

const { TextArea } = Input;
const { Text } = Typography;

export default function StoryManagement({
  selectedBrand,
  contentType,
  title,
  showSubTitle = false,
  showImage = true,
  size = "이미지 사이즈",
}) {
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.common.loading);
  const setIsLoading = (value) => dispatch(setLoading(value));

  // 홈 브랜드 스토리 상태
  const [brandStory, setBrandStory] = useState({
    id: null,
    image: null,
    imagePreview: null,
    title: "",
    subTitle: "",
    description: "",
  });
  const [brandStoryPreviewVisible, setBrandStoryPreviewVisible] =
    useState(false);
  const [previewContent, setPreviewContent] = useState("");
  console.log("subtitle", brandStory.subTitle);
  /**
   * 스토리 데이터 조회
   */
  useEffect(() => {
    const fetchStory = async () => {
      if (!selectedBrand?.id) return;

      const param = {
        brandId: selectedBrand.id,
        contentType: contentType,
      };
      const res = await getBrandContentsByType(param);
      if (res && res.length > 0) {
        const storyContent = res[0]; // 첫 번째 스토리 데이터 사용
        // 기존 timestamp 제거 (있다면)
        const cleanUrl = storyContent?.contentUrl?.split("?")[0];
        const timestamp = Date.now();

        setBrandStory({
          id: storyContent.id,
          image: storyContent.contentUrl,
          imagePreview: cleanUrl + "?timestamp=" + timestamp, // 타임스탬프 추가로 캐싱 방지
          title: storyContent.title || "",
          subTitle: storyContent.subTitle || "",
          description: storyContent.description || "",
        });
      } else {
        setBrandStory({
          id: null,
          image: null,
          imagePreview: null,
          title: "",
          subTitle: "",
          description: "",
        });
      }
    };
    fetchStory();
  }, [selectedBrand]);

  /**
   * 브랜드 스토리 이미지 업로드
   */
  const handleBrandStoryImageUpload = (file) => {
    if (!file.type.startsWith("image/")) {
      message.error("이미지 파일만 업로드 가능합니다.");
      return false;
    }

    const imageUrl = URL.createObjectURL(file);
    setBrandStory({
      ...brandStory,
      image: file,
      imagePreview: imageUrl,
    });

    return false; // 자동 업로드 방지
  };

  /**
   * 브랜드 스토리 미리보기
   */
  const previewBrandStory = () => {
    if (brandStory.imagePreview) {
      setPreviewContent(brandStory.imagePreview);
      setBrandStoryPreviewVisible(true);
    } else {
      message.warning("미리보기할 이미지가 없습니다.");
    }
  };

  /**
   * 브랜드 스토리 이미지 편집 (파일 재업로드)
   */
  const editBrandStoryImage = (e) => {
    e.stopPropagation(); // 이벤트 전파 중지

    // 파일 입력 엘리먼트 생성
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "image/*";
    fileInput.style.display = "none";

    fileInput.onchange = (event) => {
      const file = event.target.files[0];
      if (file) {
        handleBrandStoryImageUpload(file);
      }
    };

    document.body.appendChild(fileInput);
    fileInput.click();
    document.body.removeChild(fileInput);
  };

  // S3 업로드
  const s3Upload = async (file, order, deviceType) => {
    try {
      let s3Path = "";
      const timestamp = Date.now();

      // 홈 배너 경로 설정 (ProductRegisterComponent와 동일한 형식)
      if (deviceType === "pc") {
        s3Path = `brand/${selectedBrand.brand_name_en}/${contentType}/banner_${order}`;
      } else if (deviceType === "mobile") {
        s3Path = `brand/${selectedBrand.brand_name_en}/${contentType}/banner_${order}_mobile`;
      }

      let params = {
        Bucket: process.env.REACT_APP_S3_BUCKETNAME,
        Key: s3Path,
        Body: file,
      };

      const res = await plutusS3.putObject(params).promise();

      // ProductRegisterComponent와 동일한 성공 확인 방식
      if (res.success === true) {
        // S3 URL 생성
        const s3Url = `https://${process.env.REACT_APP_S3_BUCKETNAME}.s3.${process.env.REACT_APP_S3_REGION}.amazonaws.com/${s3Path}`;
        return s3Url;
      }
      return null;
    } catch (error) {
      console.error("S3 업로드 오류:", error);
      throw error;
    }
  };

  const saveBrandStoryContent = async () => {
    try {
      // 브랜드 스토리 유효성 검사
      // if (
      //   !brandStory.image ||
      //   !brandStory.title.trim() ||
      //   !brandStory.description.trim()
      // ) {
      //   message.warning(
      //     "브랜드 스토리의  제목, 설명은 필수 항목입니다.",
      //   );
      //   return;
      // }

      // 서브타이틀이 표시되는 경우에만 필수 검사 (선택적)
      if (showSubTitle && brandStory.subTitle && !brandStory.subTitle.trim()) {
        message.warning("서브타이틀을 입력하거나 비워두세요.");
        return;
      }

      setIsLoading(true);

      let pcContentUrl = "";
      // brandStory.image가 File 객체인지 확인 (새로 업로드된 파일)
      if (brandStory.image instanceof File) {
        pcContentUrl = await s3Upload(brandStory.image, 1, "pc");
        if (!pcContentUrl) {
          throw new Error(`브랜드 스토리 이미지 업로드 실패`);
        }
      } else {
        // 기존 URL 사용
        pcContentUrl = brandStory.image;
      }

      const param = {
        id: brandStory.id, // 기존 스토리면 id 포함, 신규면 null
        brandId: selectedBrand.id,
        contentType: contentType,
        mediaType: "IMAGE",
        contentUrl: pcContentUrl,
        title: brandStory.title,
        subTitle: brandStory.subTitle ?? null,
        description: brandStory.description,
        linkUrl: null,
        orderValue: 1,
        isDeleted: false,
      };

      await createBrandContent(param);
      message.success(`${title}이(가) 성공적으로 저장되었습니다.`);

      // 저장 후 스토리 데이터 새로 갱신
      const storyParam = {
        brandId: selectedBrand.id,
        contentType: contentType,
      };

      const res = await getBrandContentsByType(storyParam);
      if (res && res.length > 0) {
        const storyContent = res[0];
        // 기존 timestamp 제거 (있다면)
        const cleanUrl = storyContent?.contentUrl?.split("?")[0];
        const timestamp = Date.now();

        setBrandStory({
          id: storyContent.id,
          image: storyContent.contentUrl,
          imagePreview: cleanUrl + "?timestamp=" + timestamp, // 타임스탬프 추가로 캐싱 방지
          title: storyContent.title || "",
          subTitle: storyContent.subTitle || "",
          description: storyContent.description || "",
        });
      }
    } catch (error) {
      console.error("브랜드 스토리 저장 중 오류:", error);
      if (error.message.includes("업로드")) {
        message.error("이미지 업로드에 실패했습니다.");
      } else {
        message.error("브랜드 스토리 저장 중 오류가 발생했습니다.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* 홈 브랜드 스토리 섹션 */}
      <div className="brand-story-section">
        {/* 헤더 */}
        <div className="brand-story-section__header">
          <div>
            <h2 className="brand-story-section__header-title">{title}</h2>
            {/* <p className="brand-story-section__header-description">
              브랜드를 소개하는 스토리를 작성해보세요
            </p> */}
          </div>
          <Button
            type="primary"
            onClick={saveBrandStoryContent}
            className="save-button"
            loading={isLoading}
          >
            수정/저장
          </Button>
        </div>

        {/* 브랜드 스토리 입력 */}
        <div className="brand-story-section__content">
          {/* 이미지 업로드 */}
          {showImage && (
            <div className="brand-story-section__image-section">
              <div className="brand-story-section__image-section-title">
                <Text strong>{`${size}`}</Text>
              </div>
              {brandStory.imagePreview ? (
                // 이미지가 있을 때 - Upload 컴포넌트 없이 표시
                <div className="brand-story-upload__preview">
                  <Image
                    src={brandStory.imagePreview}
                    alt="브랜드 스토리"
                    className="brand-story-upload__preview-image"
                    preview={false}
                  />
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={(e) => editBrandStoryImage(e)}
                    className="brand-story-upload__preview-button"
                  />
                  <Button
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={previewBrandStory}
                    className="brand-story-upload__preview-button"
                    style={{ right: "38px" }}
                  />
                </div>
              ) : (
                // 이미지가 없을 때 - Upload 컴포넌트 사용
                <Upload
                  listType="picture-card"
                  showUploadList={false}
                  beforeUpload={handleBrandStoryImageUpload}
                  accept="image/*"
                  style={{ width: "100%" }}
                >
                  <div className="brand-story-upload__empty">
                    <PlusOutlined className="brand-story-upload__empty-icon" />
                    <div className="brand-story-upload__empty-text">
                      이미지 업로드
                    </div>
                  </div>
                </Upload>
              )}
            </div>
          )}

          {/* 텍스트 입력 */}
          <div className="brand-story-section__text-section">
            <div className="brand-story-section__text-section-field">
              <div className="brand-story-section__text-section-field-label">
                <Text strong>제목</Text>
              </div>
              <TextArea
                value={brandStory.title}
                onChange={(e) =>
                  setBrandStory({ ...brandStory, title: e.target.value })
                }
                placeholder="브랜드 스토리의 제목을 입력하세요"
                rows={3}
                className="brand-story-section__text-section-field-title-input"
              />
            </div>
            {showSubTitle && (
              <div className="brand-story-section__text-section-field">
                <div className="brand-story-section__text-section-field-label">
                  <Text strong>서브 타이틀</Text>
                </div>
                <TextArea
                  value={brandStory.subTitle}
                  onChange={(e) =>
                    setBrandStory({
                      ...brandStory,
                      subTitle: e.target.value,
                    })
                  }
                  placeholder="브랜드 스토리의 서브 타이틀을 입력하세요"
                  rows={8}
                  className="brand-story-section__text-section-field-description-input"
                />
              </div>
            )}
            <div className="brand-story-section__text-section-field">
              <div className="brand-story-section__text-section-field-label">
                <Text strong>설명</Text>
              </div>
              <TextArea
                value={brandStory.description}
                onChange={(e) =>
                  setBrandStory({
                    ...brandStory,
                    description: e.target.value,
                  })
                }
                placeholder="브랜드 스토리의 상세 설명을 입력하세요"
                rows={8}
                className="brand-story-section__text-section-field-description-input"
              />
            </div>
          </div>
        </div>
      </div>

      {/* 브랜드 스토리 이미지 미리보기 모달 */}
      <Modal
        visible={brandStoryPreviewVisible}
        title="브랜드 스토리 이미지"
        footer={null}
        onCancel={() => setBrandStoryPreviewVisible(false)}
        width={800}
        centered
      >
        <Image
          src={previewContent}
          alt="브랜드 스토리 미리보기"
          style={{ width: "100%" }}
        />
      </Modal>
    </>
  );
}
